import React, { useState } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, Building } from 'lucide-react';
import toast from 'react-hot-toast';
import GoogleCalendarService from '../../../../service/GoogleCalendarService';
import ApiService from '../../../../service/ApiService';
import { apiPaths } from '../../../../service/apiPaths';

interface Contact {
  _id: string;
  userId?: {
    name: string;
  };
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  platform?: {
    name: string;
  };
  interest?: {
    type: string;
  };
  services?: {
    type: string;
  };
  outputStatus?: {
    status: string;
  };
  linkedin?: string;
  createdAt?: string;
  status: "Active" | "InActive";
}

interface FollowupModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact | null;
}

const FollowupModal: React.FC<FollowupModalProps> = ({ isOpen, onClose, contact }) => {
  const [followUpDate, setFollowUpDate] = useState<string>('');
  const [followUpTime, setFollowUpTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string>('');

  if (!isOpen || !contact) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Additional validation to ensure contact exists
    if (!contact || !contact._id) {
      toast.error('Contact information is missing. Please try again.');
      return;
    }
    
    if (!followUpDate || !followUpTime) {
      toast.error('Please select both date and time for the follow-up');
      return;
    }

         setIsLoading(true);
     try {
       // Debug: Log the form values
       console.log('Form values:', { followUpDate, followUpTime, notes });
       
       // Combine date and time
       const dateTimeString = `${followUpDate}T${followUpTime}`;
       const followUpDateTime = new Date(dateTimeString);

             // Try to create Google Calendar event
       try {
         console.log('Attempting to create Google Calendar event...');
         
         // Check authentication status first
         const authStatus = await GoogleCalendarService.checkAuthenticationStatus();
         console.log('Current auth status:', authStatus);
         
         if (!authStatus.isAuthenticated) {
           console.log('Not authenticated, attempting to authenticate...');
           await GoogleCalendarService.authenticate();
           console.log('Google Calendar authentication successful');
         } else {
           console.log('Already authenticated, proceeding with event creation...');
         }

         // Get user info to confirm which account is being used
         const userInfo = await GoogleCalendarService.getUserInfo();
         setUserEmail(userInfo.email);
         console.log('Using Google Calendar account:', userInfo.email);

         // Create the follow-up event
         const event = await GoogleCalendarService.createFollowUpEvent(contact, followUpDateTime, notes);
         console.log('Google Calendar event created:', event);

         toast.success(`Follow-up event created successfully in ${userInfo.email}'s Google Calendar!`);
       } catch (googleError) {
         console.error('Google Calendar error:', googleError);
         
         // Provide more specific error messages and suggestions
         if (googleError instanceof Error) {
           if (googleError.message.includes('access_denied')) {
             toast.error('Google Calendar access denied. Please check your Google account permissions.');
           } else if (googleError.message.includes('No access token')) {
             toast.error('Please authorize your Google Calendar account first.');
           } else if (googleError.message.includes('Authentication required')) {
             toast.error('Google Calendar authentication required. Please try again.');
           } else if (googleError.message.includes('Authentication expired')) {
             toast.error('Google Calendar session expired. Please re-authenticate.');
           } else {
             toast.error(`Google Calendar error: ${googleError.message}`);
           }
         } else {
           toast.error('Google Calendar integration failed, but follow-up was recorded locally.');
         }
         
         // Try to re-authenticate if it's an authentication issue
         if (googleError instanceof Error && 
             (googleError.message.includes('Authentication') || 
              googleError.message.includes('access_denied') ||
              googleError.message.includes('No access token'))) {
           console.log('Attempting to re-authenticate...');
           try {
             await GoogleCalendarService.reAuthenticate();
             console.log('Re-authentication successful');
           } catch (reauthError) {
             console.error('Re-authentication failed:', reauthError);
           }
         }
       }

      // Always save the follow-up data to your backend
      console.log('About to save follow-up data for contact:', contact);
      await saveFollowUpToBackend(contact, followUpDateTime, notes);

      onClose();
      
      // Reset form
      setFollowUpDate('');
      setFollowUpTime('');
      setNotes('');
    } catch (error) {
      console.error('Error creating follow-up event:', error);
      toast.error('Failed to create follow-up event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to save follow-up data to your backend
  const saveFollowUpToBackend = async (contact: Contact, followUpDateTime: Date, notes: string) => {
    try {
      // Validate that we have the required data
      if (!contact || !contact._id) {
        throw new Error('Contact ID is required');
      }

      if (!apiPaths.addFollowup) {
        throw new Error('Followup endpoint not configured');
      }

      const followUpData = {
        contactId: contact._id,
        contactName: contact.name,
        followUpDate: followUpDateTime.toISOString(),
        notes: notes,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        googleCalendarAccount: userEmail // Store which Google account was used
      };

      console.log('Saving follow-up data:', followUpData);
      
             // Try to save to backend using the contact followup endpoint
       try {
         // Ensure we have valid date and time values
         if (!followUpDate || !followUpTime) {
           throw new Error('Follow-up date and time are required');
         }
         
         // Format the date and time to ensure they're in the expected format
         const formattedDate = followUpDate; // HTML date input returns YYYY-MM-DD
         const formattedTime = followUpTime; // HTML time input returns HH:MM
         
         console.log('Formatted values:', { formattedDate, formattedTime });
         
         const apiData = {
           followUpDate: formattedDate,
           followUpTime: formattedTime,
           notes: notes || '',
           contactId: contact._id,
           contactName: contact.name,
           status: 'scheduled',
           googleCalendarAccount: userEmail
         };
         
         console.log('Sending data to addFollowup API:', apiData);
         console.log('API endpoint:', apiPaths.addFollowup.replace(':id', contact._id));
         
         try {
           await ApiService({
             method: 'POST',
             endpoint: apiPaths.addFollowup.replace(':id', contact._id),
             data: apiData
           });
           
           toast.success('Follow-up scheduled successfully!');
                  } catch (apiError) {
           console.warn('First attempt failed, trying alternative field names:', apiError);
           console.log('API Error details:', {
             message: apiError instanceof Error ? apiError.message : 'Unknown error',
             error: apiError
           });
           
           // Try alternative field names that the backend might expect
           const alternativeData = {
             followupDate: formattedDate, // Alternative: lowercase 'u'
             followupTime: formattedTime, // Alternative: lowercase 'u'
             followUpDateTime: followUpDateTime.toISOString(), // Alternative: combined datetime
             notes: notes || '',
             contactId: contact._id,
             contactName: contact.name,
             status: 'scheduled',
             googleCalendarAccount: userEmail || ''
           };
           
           console.log('Trying alternative data structure:', alternativeData);
           
           try {
             await ApiService({
               method: 'POST',
               endpoint: apiPaths.addFollowup.replace(':id', contact._id),
               data: alternativeData
             });
             
             toast.success('Follow-up scheduled successfully! (Alternative format)');
           } catch (secondError) {
             console.error('Second attempt also failed:', secondError);
             throw secondError; // Re-throw to trigger the fallback
           }
         }
       } catch (followupError) {
        console.warn('Followup endpoint failed, trying notes endpoint as fallback:', followupError);
        
                 // Fallback to using the notes endpoint
         await ApiService({
           method: 'POST',
           endpoint: apiPaths.addNotes.replace(':id', contact._id),
           data: {
             notes: `FOLLOW-UP SCHEDULED: ${followUpDate} at ${followUpTime}\n${notes}\nGoogle Calendar: ${userEmail || 'Not connected'}`,
             type: 'followup',
             followUpDate: followUpDate,
             followUpTime: followUpTime,
             status: 'scheduled'
           }
         });
        
        toast.success('Follow-up scheduled successfully! (Saved as note)');
      }
    } catch (error) {
      console.error('Error saving follow-up to backend:', error);
      
      // Show user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('Contact ID is required')) {
          toast.error('Contact information is missing. Please try again.');
        } else if (error.message.includes('Followup endpoint not configured')) {
          toast.error('System configuration error. Please contact support.');
        } else {
          toast.error('Failed to save follow-up. Please try again.');
        }
      } else {
        toast.error('Failed to save follow-up. Please try again.');
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setFollowUpDate('');
      setFollowUpTime('');
      setNotes('');
      setUserEmail('');
    }
  };

  // Get current date and time for default values
  const now = new Date();
  const currentDate = now.toISOString().split('T')[0];
  const currentTime = now.toTimeString().slice(0, 5);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
                     <div className="flex items-center space-x-2">
             <Calendar className="w-5 h-5 text-blue-600" />
             <h2 className="text-xl font-semibold text-gray-900">Schedule Follow-up</h2>
           </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contact Info */}
        <div className="p-6 border-b bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Contact Details</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">{contact.name}</span>
            </div>
            {contact.email && (
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-900">{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-900">{contact.phone}</span>
              </div>
            )}
            {contact.companyName && (
              <div className="flex items-center space-x-2">
                <Building className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-900">{contact.companyName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                min={currentDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <input
                type="time"
                value={followUpTime}
                onChange={(e) => setFollowUpTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

                     {/* Notes */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Additional Notes (Optional)
             </label>
             <textarea
               value={notes}
               onChange={(e) => setNotes(e.target.value)}
               rows={3}
               placeholder="Add any additional notes for this follow-up..."
               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
             />
           </div>

           {/* Google Calendar Status Check */}
           {/* <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
             <div className="flex items-center justify-between mb-2">
               <span className="text-sm font-medium text-blue-800">Google Calendar Status</span>
               <button
                 type="button"
                 onClick={async () => {
                   try {
                     const status = await GoogleCalendarService.checkAuthenticationStatus();
                     console.log('Google Calendar Status:', status);
                     alert(`Google Calendar Status:\n${status.message}\n\nHas Token: ${status.hasToken}\nToken Valid: ${status.tokenValid}\nGAPI Ready: ${status.gapiReady}`);
                   } catch (error) {
                     console.error('Error checking status:', error);
                     alert('Error checking Google Calendar status');
                   }
                 }}
                 className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
               >
                 Check Status
               </button>
             </div>
             <p className="text-xs text-blue-600">
               Click "Check Status" to verify your Google Calendar connection before scheduling.
             </p>
           </div> */}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  <span>Schedule Follow-up</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FollowupModal; 